import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../../utils/slug';
import { Event } from '../../types/events';
import { EVENT_EMOJI, EVENT_GRADIENT } from '../../constants/events';
import { UserCoords, calcDistance, formatDistance } from '../../hooks/useUserLocation';

interface EventCardProps {
  event: Event;
  userCoords?: UserCoords | null;
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export default function EventCard({ event, userCoords }: EventCardProps) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const distance = userCoords && event.latitude && event.longitude
    ? formatDistance(calcDistance(userCoords.lat, userCoords.lng, event.latitude, event.longitude))
    : null;

  const emoji = EVENT_EMOJI[event.category] || '📅';
  const gradient = EVENT_GRADIENT[event.category] || EVENT_GRADIENT['Other'];
  const images = event.images?.filter(Boolean) || [];
  const hasImage = images.length > 0;
  const multiImage = images.length > 1;

  return (
    <div
      onClick={() => navigate(`/events/${slugify(event.title)}-${event.id}?community=${encodeURIComponent(event.community || '')}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: '#ffffff',
        border: hovered ? '1.5px solid #FF385C' : '1.5px solid #eef0f6',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hovered ? '0 12px 40px rgba(255,56,92,0.12)' : '0 2px 12px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image */}
      <div style={{ width: '100%', aspectRatio: '16/9', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        {hasImage ? (
          <>
            <img src={images[imgIndex]} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s', transform: hovered ? 'scale(1.04)' : 'scale(1)' }} />
            {multiImage && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setImgIndex(i => (i - 1 + images.length) % images.length); }} style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>‹</button>
                <button onClick={(e) => { e.stopPropagation(); setImgIndex(i => (i + 1) % images.length); }} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>›</button>
                <div style={{ position: 'absolute', bottom: '6px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '4px' }}>
                  {images.map((_, i) => <div key={i} onClick={(e) => { e.stopPropagation(); setImgIndex(i); }} style={{ width: i === imgIndex ? '14px' : '6px', height: '6px', borderRadius: '3px', cursor: 'pointer', backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.55)', transition: 'all 0.2s' }} />)}
                </div>
              </>
            )}
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}>{emoji}</span>
          </div>
        )}
        {/* Date badge */}
        <div style={{ position: 'absolute', top: '8px', left: '8px', backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', borderRadius: '8px', padding: '4px 8px', fontSize: '0.68rem', fontWeight: 700, color: '#111', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
          {new Date(event.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </div>
        <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
          {event.is_private && (
            <span style={{ backgroundColor: 'rgba(255,255,255,0.92)', color: '#92400e', fontSize: '0.65rem', fontWeight: 600, padding: '3px 8px', borderRadius: '10px', backdropFilter: 'blur(4px)', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>🔒 Private</span>
          )}
          {event.status === 'suspended' && (
            <span style={{ backgroundColor: '#ef4444', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: '10px' }}>Suspended</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 0 0.25rem', minWidth: 0 }}>
          <span style={{ color: '#6b7a99', fontSize: '0.78rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>📍 {event.location}</span>
          {distance && <span style={{ color: '#10b981', fontSize: '0.78rem', fontWeight: 600, flexShrink: 0 }}>{distance}</span>}
        </div>
        <p style={{ color: '#6b7a99', fontSize: '0.82rem', margin: '0 0 0.75rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.5', flex: 1 }}>{event.description}</p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid #f0f2f7', marginTop: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>{formatDate(event.event_date)}</span>
            <span style={{ color: '#a0aec0', fontSize: '0.72rem' }}>
              {event.attendees_count != null && `👥 ${event.attendees_count}${event.max_attendees ? `/${event.max_attendees}` : ''}`}
              {event.attendees_count != null && event.community && ' · '}
              {event.community}
            </span>
          </div>
          <span style={{ backgroundColor: '#f5f7ff', color: '#4a5568', fontSize: '0.72rem', padding: '3px 10px', borderRadius: '12px', fontWeight: 500, border: '1px solid #eef0f6' }}>{emoji} {event.category}</span>
        </div>
      </div>
    </div>
  );
}
